import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LabelList,
} from 'recharts';
import {
  ChevronLeft, ChevronRight, DollarSign, Hash, TrendingUp, Calendar,
  User, Building2, MapPin, ExternalLink, Briefcase, Printer,
} from 'lucide-react';
import {
  getPartyColor, getPartyTextClass, getPieColor,
  COLORS, PARTY_COLORS, PARTY_THEME,
  formatCurrency, formatCurrencyShort,
} from '../../utils/partyUtils';
import { generatePrintReport } from '../../utils/printReport';

export default function SummaryPanel({
  summary,
  topDonors,
  topRecipients,
  filters,
  currentIndex,
  navigateTo,
  displayTimeRange,
  setRedfin,
  setGoogleSearch,
  sortBy,
  sortOrder,
}) {
  const [, setSearchParams] = useSearchParams();
  const [reportLoading, setReportLoading] = useState(false);

  if (!summary) return null;

  const isDonor = summary.type === 'donor';
  const relatedData = isDonor ? summary.top_recipients : summary.top_donors;
  const pieData = relatedData?.slice(0, 6).map((item) => ({
    name: item.name.length > 20 ? item.name.substring(0, 20) + '...' : item.name,
    value: item.total,
    fullName: item.name,
  })) || [];

  const dominantParty = isDonor ? summary.dominant_party : null;
  const pc = dominantParty ? PARTY_THEME[dominantParty] : null;

  const list = isDonor ? topDonors : topRecipients;
  const canNavigate = list.length > 1 && !filters.donor && !filters.recipient;

  const handlePrint = async () => {
    setReportLoading(true);
    try {
      await generatePrintReport({ summary, sortBy, sortOrder, displayTimeRange });
    } catch (error) {
      console.error("Failed to generate report:", error);
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="summary-panel">
      {/* Summary Header with Navigation */}
      <Card className={`border rounded-sm shadow-sm bg-gradient-to-r ${pc ? pc.gradient : 'from-slate-50 to-white'} ${pc ? pc.border : ''}`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            {canNavigate && (
              <Button variant="outline" size="icon" onClick={() => navigateTo(-1)} className="rounded-sm h-12 w-12 shrink-0" data-testid="nav-prev">
                <ChevronLeft className="w-6 h-6" />
              </Button>
            )}

            <div className="flex items-center gap-4 flex-1 mx-6">
              <div className={`p-3 rounded-sm ${pc ? pc.bg : 'bg-primary'}`}>
                {isDonor ? <User className="w-6 h-6 text-white" /> : <Building2 className="w-6 h-6 text-white" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">
                    {isDonor ? 'Donor Profile' : 'Recipient Profile'}
                  </p>
                  {canNavigate && currentIndex >= 0 && (
                    <span className="text-xs text-muted-foreground font-mono">({currentIndex + 1} of {list.length})</span>
                  )}
                  {pc && (
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm ${pc.bgLight} ${pc.text} ${pc.border} border`} data-testid="party-badge">
                      {pc.label}
                    </span>
                  )}
                </div>
                <h2 className="font-heading text-2xl font-semibold tracking-tight mt-1">
                  <button
                    onClick={() => setGoogleSearch({ name: summary.name, address: summary.address })}
                    className={`hover:underline decoration-dotted underline-offset-4 cursor-pointer transition-colors text-left ${pc ? `hover:${pc.text}` : 'hover:text-primary'}`}
                    data-testid="donor-name-search-link"
                  >
                    {summary.name}
                  </button>
                </h2>
                <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{summary.year_range}</span>
                  {summary.address && (
                    <button
                      onClick={() => setRedfin(summary.address)}
                      className="flex items-center gap-1 hover:text-primary transition-colors cursor-pointer"
                      data-testid="address-redfin-link"
                    >
                      <MapPin className="w-3 h-3" />
                      <span className="underline decoration-dotted underline-offset-2">{summary.address}</span>
                      <ExternalLink className="w-3 h-3 opacity-50" />
                    </button>
                  )}
                  {!summary.address && summary.states && summary.states.length > 0 && (
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{summary.states.slice(0, 3).join(', ')}</span>
                  )}
                </div>
                {(summary.occupation || summary.employer) && (
                  <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                    {summary.occupation && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{summary.occupation}</span>}
                    {summary.employer && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{summary.employer}</span>}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={handlePrint} disabled={reportLoading} className="rounded-sm h-10 gap-2" data-testid="print-report-btn">
                {reportLoading ? <div className="w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" /> : <Printer className="w-4 h-4" />}
                <span className="hidden sm:inline text-xs">{reportLoading ? 'Loading...' : 'Print Report'}</span>
              </Button>
              {canNavigate && (
                <Button variant="outline" size="icon" onClick={() => navigateTo(1)} className="rounded-sm h-12 w-12 shrink-0" data-testid="nav-next">
                  <ChevronRight className="w-6 h-6" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: DollarSign, label: 'Total', value: formatCurrencyShort(summary.total_amount), valueClass: pc ? pc.text : 'text-emerald-600' },
          { icon: Hash, label: 'Donations', value: summary.donation_count?.toLocaleString() },
          { icon: TrendingUp, label: 'Average', value: formatCurrency(summary.avg_donation || 0) },
          { icon: isDonor ? Building2 : User, label: isDonor ? 'Recipients' : 'Donors', value: isDonor ? relatedData?.length || 0 : summary.unique_donor_count || 0 },
        ].map((stat, i) => (
          <Card key={i} className={`border rounded-sm shadow-sm ${pc ? `${pc.border} border-l-4` : ''}`} style={pc ? { borderLeftColor: pc.accent } : undefined}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <stat.icon className="w-4 h-4" />
                <span className="text-xs uppercase tracking-wider">{stat.label}</span>
              </div>
              <p className={`font-mono text-xl font-semibold ${stat.valueClass || ''}`}>{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <YearChart summary={summary} isDonor={isDonor} pc={pc} displayTimeRange={displayTimeRange} />
        <RelatedPieChart
          isDonor={isDonor}
          pieData={pieData}
          dominantParty={dominantParty}
          setSearchParams={setSearchParams}
        />
      </div>
    </div>
  );
}

function YearChart({ summary, isDonor, pc, displayTimeRange }) {
  const hasPartyData = isDonor && summary.by_year_party && summary.by_year_party.length > 0;
  const yearData = hasPartyData
    ? summary.by_year_party.filter(d => d.year >= displayTimeRange.start && d.year <= displayTimeRange.end)
    : summary.by_year?.filter(d => d.year >= displayTimeRange.start && d.year <= displayTimeRange.end) || [];

  if (yearData.length === 0) {
    return (
      <Card className="border rounded-sm shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="font-heading text-sm font-medium">Donations by Year</CardTitle></CardHeader>
        <CardContent><div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">No yearly data</div></CardContent>
      </Card>
    );
  }

  const hasDem = hasPartyData && yearData.some(d => d.democrat > 0);
  const hasRep = hasPartyData && yearData.some(d => d.republican > 0);
  const hasOther = hasPartyData && yearData.some(d => d.other > 0);
  const topBar = hasOther ? 'other' : hasRep ? 'republican' : 'democrat';

  return (
    <Card className="border rounded-sm shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-sm font-medium flex items-center justify-between">
          <span>Donations by Year</span>
          {hasPartyData && (
            <div className="flex items-center gap-3 text-xs font-normal">
              {hasDem && <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-500" />Democrat</span>}
              {hasRep && <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-500" />Republican</span>}
              {hasOther && <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full" style={{ backgroundColor: pc ? pc.accent : '#94a3b8' }} />{pc ? pc.label.split(' ')[0] : 'Other'}</span>}
              <span className="flex items-center gap-1"><div className="w-4 h-0.5 bg-green-600 rounded" />#</span>
            </div>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={yearData} margin={{ top: 20, right: 40, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="year" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={{ stroke: 'hsl(var(--border))' }} tickLine={false} />
            <YAxis yAxisId="amount" tickFormatter={formatCurrencyShort} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} width={50} />
            <YAxis yAxisId="count" orientation="right" tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} tick={{ fontSize: 10, fill: '#16a34a' }} axisLine={false} tickLine={false} width={35} />
            <Tooltip
              formatter={(value, name) => {
                if (name === 'count') return [value.toLocaleString(), '# Donations'];
                if (hasPartyData) {
                  const label = name === 'democrat' ? 'Democrat' : name === 'republican' ? 'Republican' : 'Other';
                  return [formatCurrency(value), label];
                }
                return [formatCurrency(value), "Total"];
              }}
              contentStyle={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: '2px', fontSize: '11px' }}
            />
            {hasPartyData ? (
              <>
                {hasDem && <Bar yAxisId="amount" dataKey="democrat" stackId="a" fill="#2563eb" name="democrat" radius={topBar === 'democrat' ? [2,2,0,0] : undefined}>
                  {topBar === 'democrat' && <LabelList dataKey="total" position="top" formatter={formatCurrencyShort} style={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }} />}
                </Bar>}
                {hasRep && <Bar yAxisId="amount" dataKey="republican" stackId="a" fill="#dc2626" name="republican" radius={topBar === 'republican' ? [2,2,0,0] : undefined}>
                  {topBar === 'republican' && <LabelList dataKey="total" position="top" formatter={formatCurrencyShort} style={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }} />}
                </Bar>}
                {hasOther && <Bar yAxisId="amount" dataKey="other" stackId="a" fill={pc ? pc.accent : '#94a3b8'} name="other" radius={[2,2,0,0]}>
                  <LabelList dataKey="total" position="top" formatter={formatCurrencyShort} style={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }} />
                </Bar>}
              </>
            ) : (
              <Bar yAxisId="amount" dataKey="total" fill={pc ? pc.accent : 'hsl(var(--primary))'} radius={[2, 2, 0, 0]}>
                <LabelList dataKey="total" position="top" formatter={formatCurrencyShort} style={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }} />
              </Bar>
            )}
            <Line yAxisId="count" type="monotone" dataKey="count" stroke="#16a34a" strokeWidth={2} dot={{ r: 3, fill: '#16a34a' }} name="count" />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function RelatedPieChart({ isDonor, pieData, dominantParty, setSearchParams }) {
  if (pieData.length === 0) {
    return (
      <Card className="border rounded-sm shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="font-heading text-sm font-medium">Top {isDonor ? 'Recipients' : 'Donors'}</CardTitle></CardHeader>
        <CardContent><div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">No data available</div></CardContent>
      </Card>
    );
  }

  const chartColors = (dominantParty && PARTY_COLORS[dominantParty]) || COLORS;

  return (
    <Card className="border rounded-sm shadow-sm">
      <CardHeader className="pb-2"><CardTitle className="font-heading text-sm font-medium">Top {isDonor ? 'Recipients' : 'Donors'}</CardTitle></CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          <ResponsiveContainer width="50%" height={200}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value">
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getPieColor(entry.fullName, index, chartColors)} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name, props) => [formatCurrency(value), props.payload.fullName]}
                contentStyle={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: '2px', fontSize: '11px' }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex-1 space-y-1">
            {pieData.slice(0, 5).map((item, index) => {
              const partyTextClass = getPartyTextClass(item.fullName);
              const isClickable = !isDonor;
              return (
                <div key={index} className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: getPieColor(item.fullName, index, chartColors) }} />
                  {isClickable ? (
                    <button
                      onClick={() => setSearchParams({ donor: item.fullName })}
                      className={`truncate flex-1 text-left hover:underline cursor-pointer ${partyTextClass || 'text-blue-600 hover:text-blue-800'}`}
                      title={item.fullName}
                      data-testid={`pie-donor-link-${index}`}
                    >
                      {item.name}
                    </button>
                  ) : (
                    <span className={`truncate flex-1 ${partyTextClass || ''}`} title={item.fullName}>{item.name}</span>
                  )}
                  <span className={`font-mono shrink-0 ${partyTextClass || 'text-muted-foreground'}`}>{formatCurrencyShort(item.value)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
